import * as React from 'react';
import { SearchClient } from 'algoliasearch';
import { SearchResponse } from '@algolia/client-search';
import {
  ZipCodeHit,
  QuerySuggestions,
  LatLong,
  LocalDealerData,
  UserLocationInfo,
  PartnerDescription,
  DeviceType,
  AlgoliaIndiceName,
} from '@packages/common';
import { getPublicRuntimeConfig } from '@helpers/getPublicRuntimeConfig';

import {
  productSearch,
  querySuggestionSearch,
  localMerchantsSearchByPosition,
  localDealerSearchById,
  zipCodeSearch,
  createSearchBase,
  ZipCodeSearchParam,
  QuerySuggestionSearchParams,
  ProductSearch,
  branchStoresSearch,
  localMerchantsWithAdsSearchByIp,
} from './helpers';
import { createAlgoliaTrackingAgent, AlgoliaTrackingAgent } from './tracking';

const APP_TLD = getPublicRuntimeConfig('APP_TLD');
const ALGOLIA_APPLICATION_ID_1 = getPublicRuntimeConfig('ALGOLIA_APPLICATION_ID_1');
const ALGOLIA_APPLICATION_ID_2 = getPublicRuntimeConfig('ALGOLIA_APPLICATION_ID_2');
const ALGOLIA_SEARCH_API_KEY_1 = getPublicRuntimeConfig('ALGOLIA_APPLICATION_ID_1');
const ALGOLIA_SEARCH_API_KEY_2 = getPublicRuntimeConfig('ALGOLIA_APPLICATION_ID_2');

interface AlgoliaSearchHelpers {
  productSearch: ProductSearch;
  querySuggestionSearch: (params: QuerySuggestionSearchParams) => Promise<SearchResponse<QuerySuggestions> | null>;
  localMerchantsSearch: (
    pos: UserLocationInfo,
  ) => Promise<
    (LocalDealerData & { coords: LatLong; key: string; name: string; isOpenNow: boolean; nextOpenTime: any })[]
  >;
  localDealerSearchById: (dealerObjectId: string) => Promise<PartnerDescription>;
  localMerchantsWithAdsSearchByIp: () => Promise<LocalDealerData[] | null>;
  zipCodeSearch: (searchParam: ZipCodeSearchParam, hitsPerPage?: number) => Promise<SearchResponse<ZipCodeHit> | null>;
  branchStoresSearch: (coords: LatLong) => Readonly<
    Promise<
      SearchResponse<{
        postalCode: string;
      }>
    >
  >;
}

interface AlgoliaContextValue extends AlgoliaSearchHelpers {
  trackingAgentBySearchIndex: (indice: AlgoliaIndiceName) => AlgoliaTrackingAgent;
}

interface AlgoliaProviderProps {
  algoliaClientByIndexName: (indexName: AlgoliaIndiceName) => SearchClient;
  deviceType?: DeviceType;
  userIdHash?: string;
}

export const AlgoliaContext = React.createContext({} as AlgoliaContextValue);

export const AlgoliaProvider: React.FC<AlgoliaProviderProps> = ({
  children,
  algoliaClientByIndexName,
  deviceType = DeviceType.desktop,
  userIdHash = '',
}) => {
  const searchBase = createSearchBase(algoliaClientByIndexName);
  const hasSplitAlgoliaClient = ALGOLIA_APPLICATION_ID_2 && ALGOLIA_SEARCH_API_KEY_2;

  const trackingAgent1 = React.useRef(
    createAlgoliaTrackingAgent({
      appId: ALGOLIA_APPLICATION_ID_1,
      apiKey: ALGOLIA_SEARCH_API_KEY_1,
      userToken: userIdHash,
    }),
  );

  const trackingAgent2 = React.useRef(
    hasSplitAlgoliaClient
      ? createAlgoliaTrackingAgent({
          appId: ALGOLIA_APPLICATION_ID_2,
          apiKey: ALGOLIA_SEARCH_API_KEY_2,
          userToken: userIdHash,
        })
      : trackingAgent1,
  );

  const trackingAgentBySearchIndex = React.useCallback(
    (index: AlgoliaIndiceName) => {
      return (
        {
          [AlgoliaIndiceName.PRODUCTS_PRICE_ASC]: trackingAgent2.current,
          [AlgoliaIndiceName.PRODUCTS_PRICE_DESC]: trackingAgent2.current,
        }[index] || trackingAgent1.current
      );
    },
    [trackingAgent1, trackingAgent2],
  );

  return (
    <AlgoliaContext.Provider
      value={{
        zipCodeSearch: zipCodeSearch(searchBase),
        querySuggestionSearch: querySuggestionSearch(searchBase),
        productSearch: productSearch(searchBase, userIdHash, deviceType),
        localMerchantsSearch: localMerchantsSearchByPosition(searchBase, APP_TLD),
        localDealerSearchById: localDealerSearchById(searchBase),
        localMerchantsWithAdsSearchByIp: localMerchantsWithAdsSearchByIp(searchBase, APP_TLD),
        branchStoresSearch: branchStoresSearch(searchBase),
        trackingAgentBySearchIndex,
      }}
    >
      {children}
    </AlgoliaContext.Provider>
  );
};
